import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import bind from 'bind-decorator';
import {IMeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignUIState';

export type MeshLocationKey = string;

export class MeshLocation implements IMeshLocation
{
    public constructor(public readonly colIndex:number,
                       public readonly rowIndex:number)
    {
    }

    @bind
    toJson()
    {
        return {
            colIndex: this.colIndex,
            rowIndex: this.rowIndex
        };
    }

    public static fromSlotLocation(location:SlotLocation):MeshLocation
    {
        return new MeshLocation(location.colIndex, location.rowIndex);
    }

    public static fromKey(key:MeshLocationKey):MeshLocation
    {
        let [colIndex, rowIndex] = key.split('_').map(num => parseInt(num, 10));

        if( isNaN(colIndex) )
            colIndex = -1;

        if( isNaN(rowIndex) )
            rowIndex = -1;

        return new MeshLocation(colIndex, rowIndex);
    }

    public toSlotLocation():SlotLocation
    {
        return new SlotLocation(this.colIndex, this.rowIndex);
    }

    public withDefaults(defaultCol:number = 0, defaultRow:number = 0):MeshLocation
    {
        if( this.rowIndex != null && this.colIndex != null )
            return this;

        return new MeshLocation(this.colIndex || defaultCol, this.rowIndex || defaultRow);
    }

    public makeCopyWithCol(colIndex:number)
    {
        return new MeshLocation(colIndex, this.rowIndex);
    }

    public makeCopyWithRow(rowIndex:number)
    {
        return new MeshLocation(this.colIndex, rowIndex);
    }

    public translate(deltaRows:number, deltaCols:number)
    {
        return new MeshLocation(this.colIndex + deltaCols, this.rowIndex + deltaRows);
    }

    public makeCopyAndMove(colDelta:number, rowDelta:number)
    {
        return this.translate(rowDelta, colDelta);
    }

    public asKey():MeshLocationKey
    {
        return MeshLocation.createKey(this.colIndex, this.rowIndex);
    }

    public isEqualTo(other:MeshLocation):boolean
    {
        return other && other.rowIndex === this.rowIndex && other.colIndex === this.colIndex;
    }

    public compareTo(other:MeshLocation):number
    {
        if( this.rowIndex < other.rowIndex ) return -1;
        if( this.rowIndex > other.rowIndex ) return 1;

        if( this.colIndex < other.colIndex ) return -1;
        if( this.colIndex > other.colIndex ) return 1;

        return 0;
    }

    public static createKey(colIndex:number, rowIndex:number):MeshLocationKey
    {
        return `${colIndex}_${rowIndex}`;
    }

    public toDesignUIState():IMeshLocation
    {
        return {
            colIndex: this.colIndex,
            rowIndex: this.rowIndex
        }
    }

    public fromDesignUIState(state:IMeshLocation):void
    {
        this.withDefaults(state.colIndex, state.rowIndex)
    }
}
